import { LogData, logSentryError as logSentryErrorFramework, SENTRY_ERROR_FILTERS } from '@mews/framework';
import { DateTimeUtcService } from '@mews/framework/datetime';
import { DistributorThunkAction } from '../types';
import { getAppConfiguration } from '../modules/configuration/selectors';

const getErrorsCausedByBots = (): RegExp[] =>
    [
        /*
         * These patterns occur in the user agent string and they identify bots which cause Azure App Insights and New Relic endpoints to return 400 Bad Request.
         * We don't have to take care about false positives because if something unrelated gets broken we will get reports from other browsers.
         */
        // https://mewssystems.sentry.io/issues/3828373467/events/add1ba3b94c94e88a5d17d4b806ec6d2/
        /\+https:\/\/naver\.me\/spd/,
        /**
         * - Azure App Insights - https://mewssystems.sentry.io/issues/3828373467/events/6b3186160fce435e8e56334ade4cb116/
         * - New Relic - https://mewssystems.sentry.io/issues/3828373467/events/01919011f17e42a7b5b8c140b05ffac0/
         */
        /\+https:\/\/yahoo\.jp\/3BSZgF/,
    ].some((pattern) => pattern.test(window.navigator.userAgent))
        ? [/^BadRequestError: Bad Request$/]
        : [];

export const ignoredErrors: RegExp[] = [
    ...getErrorsCausedByBots(),
    /**
     * Unfixable: https://github.com/getsentry/sentry-javascript/issues/2514
     * The start of the issue coincided with the launch of reCAPTCHA.
     * This was verified in LaunchDarkly when googleRecaptchaV3VerificationEnabled was turned on.
     */
    /Non-Error promise rejection captured with value: Timeout$/,
    /^Error: user cookie is not initialize$/,
    /^Non-Error promise rejection captured with value:\s?$/,
    /^UnhandledRejection: Non-Error promise rejection captured with value:\s?$/,
    /^NotAuthorizedError: Cannot perform operation or session has expired/,
    /^NotAuthorizedError: Unhandled Promise Rejection: Cannot perform operation or session has expired/,
    /^BadRequestError: Invalid Ids/,
    /^BadRequestError: Invalid PrimaryId or Ids/,
    /^BadRequestError: Operation is not enabled on your current subscription/,
    /^BadRequestError: PrimaryId or Ids must be provided\./,
    /^BusinessError: Multiple enterprise booking engine is not allowed for one or more enterprises/,
    /^NS_ERROR_FAILURE: No error message/,
    /^NS_ERROR_FILE_CORRUPTED: No error message/,
    /^NS_ERROR_NOT_INITIALIZED: No error message/,
    /Non-Error promise rejection captured with value: Object Not Found Matching Id:/,
    /.*evaluating '.*webkit.messageHandlers'.*/,
    /.*evaluating '.*__gCrWeb.fill.setUniqueIDIfNeeded.*/,
    /^BusinessError: Bad Gateway/,
    /^BusinessError: Internal Server Error/,
    /^TimeoutError: Gateway Timeout/,
    /^BusinessError: Transaction has timed out/,
    /^BusinessError: error$/,
    /^BusinessError: Unhandled Promise Rejection: error$/,
    /^BusinessError: No error message$/,
    /^BusinessError: Unexpected error occurred$/,
    /^BusinessError: Unhandled Promise Rejection: Unexpected error occurred$/,
    /^BusinessError: Service Unavailable$/,
    /^BusinessError: <unknown status code>$/,
    // Error: "Should not already be working". https://github.com/facebook/react/issues/17355
    /^Error: Minified React error #327; visit https:\/\/reactjs\.org\/docs\/error-decoder\.html\?invariant=327/,
    /SecurityError: Permission denied to access property "CookieFirst" on cross-origin object$/,
    // LaunchDarkly client SDK contain a bug where it does not handle localStorage error - therefore we will ignore this error until this is fixed https://github.com/launchdarkly/js-sdk-common/issues/54
    /'document\.getElementsByTagName\('video'\)\[0]'/,
    /^SecurityError: Permission denied to access property.*/,
    /^TypeError: Cannot read properties of undefined \(reading 'attributes'\)$/,
    /^TypeError: Cannot read properties of undefined \(reading 'bookingEngines'\)$/,
    /^TypeError: Cannot read properties of undefined \(reading 'href'\)$/,
    /^TypeError: Cannot redefine property: googletag$/,
    /^SyntaxError: The string did not match the expected pattern\.$/,
    /^ReferenceError: triptease is not defined$/,
    /^ReferenceError: dataLayer is not defined$/,
    /^ReferenceError: TrackerStorageType is not defined$/,
    /NotAllowedError: The play method is not allowed by the user agent/,
];

export const getSentryIgnoreErrors = (standalone: boolean, iframed: boolean): RegExp[] => [
    /^QuotaExceededError/,
    ...ignoredErrors,
    ...(!standalone ? [/^UnhandledRejection: Non-Error promise rejection captured with value: undefined$/] : []),
    ...(!DateTimeUtcService.isSupported()
        ? [
              /^TypeError: The argument is not in a supported format: [\d]{4}-[\d]{2}-[\d]{2}T[\d]{2}:[\d]{2}:[\d]{2}Z$/,
              /^TypeError: Unable to get property 'timezone' of undefined or null reference$/,
          ]
        : []),
    // Distributor should not be loaded in another iframe. It works somehow but there are issues.
    ...(iframed ? [/^SecurityError/] : []),
    ...SENTRY_ERROR_FILTERS,
];

export const logSentryError =
    ({ error, extra }: LogData): DistributorThunkAction =>
    (_, getState) => {
        const appConfiguration = getAppConfiguration(getState());

        return logSentryErrorFramework({
            error,
            extra,
            ignoredErrors: getSentryIgnoreErrors(appConfiguration.isStandalone, appConfiguration.isIframed),
        });
    };
